/**
 * Self-test suite: Group identity (master key + invites)
 *
 * Three groups — mirrors iOS `GroupIdentitySelfTest.swift`:
 *
 *   A. Master key + public id — generation, length validation, HKDF
 *      determinism:
 *      A1. generate() produces a 32-byte masterKey
 *      A2. two independent generateGroup() calls produce different masterKeys
 *      A3. GroupMasterKey rejects a 31-byte raw value
 *      A4. GroupMasterKey rejects a 33-byte raw value
 *      A5. derivePublicId is deterministic (same masterKey → same publicId)
 *      A6. derivePublicId differs for different master keys
 *
 *   B. Group-key signing — HMAC roundtrip, tamper rejection, cross-key
 *      rejection, length-mismatch rejection:
 *      B1. sign + verify roundtrip succeeds and returns a 32-byte MAC
 *      B2. signature is a 32-byte MAC, not the payload itself
 *      B3. verify rejects a tampered payload
 *      B4. verify rejects a signature made with a different master key
 *      B5. verify rejects a truncated 31-byte signature
 *      B6. verify rejects an overlong 33-byte signature
 *
 *   C. Invite link codec — encode/decode roundtrip, prefix detection,
 *      tampered-link rejection, garbage rejection:
 *      C1. encode → decode roundtrip preserves both halves
 *      C2. encoded link starts with kaether:group:invite:v1: prefix
 *      C3. isGroupInviteLink distinguishes invite from other-codec strings
 *      C4. tryDecodeGroupInviteLink returns null for non-invite strings
 *      C5. tryDecodeGroupInviteLink returns the link for a valid invite
 *      C6. tampered invite link rejected (publicIdDoesNotMatchMasterKey)  ← CRITICAL
 *      C7. garbage base64url in invite link is rejected
 *      C8. wrong prefix → null from tryDecodeGroupInviteLink
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  GroupMasterKey,
  GroupPublicId,
  GroupIdentityError,
  generateGroup,
  derivePublicId,
  sign,
  verify,
  base64UrlEncode,
} from "./GroupIdentity";
import {
  GROUP_INVITE_PREFIX,
  isGroupInviteLink,
  encodeGroupInviteLink,
  decodeGroupInviteLink,
  tryDecodeGroupInviteLink,
} from "./GroupInviteLink";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a random 32-byte master key for test use. */
async function freshMasterKey(): Promise<GroupMasterKey> {
  const raw = new Uint8Array(32);
  crypto.getRandomValues(raw);
  return new GroupMasterKey(raw);
}

// ---------------------------------------------------------------------------
// Group A: master key + public id
// ---------------------------------------------------------------------------

async function testGenerateReturns32Bytes(): Promise<TestCase> {
  const { masterKey } = await generateGroup();
  const ok = masterKey.raw.length === 32;
  return ok
    ? pass("generate() produces a 32-byte masterKey")
    : fail(
        "generate() produces a 32-byte masterKey",
        `got ${masterKey.raw.length} bytes`,
      );
}

async function testTwoGeneratesDiffer(): Promise<TestCase> {
  const a = await freshMasterKey();
  const b = await freshMasterKey();
  const ok = !a.equals(b);
  return ok
    ? pass("two independent generateGroup() calls produce different masterKeys")
    : fail(
        "two independent generateGroup() calls produce different masterKeys",
        "RNG produced same key twice",
      );
}

async function testMasterKeyRejects31Bytes(): Promise<TestCase> {
  const name = "GroupMasterKey rejects 31-byte raw value";
  try {
    new GroupMasterKey(new Uint8Array(31));
    return fail(name, "constructor did not throw");
  } catch (err) {
    if (
      err instanceof GroupIdentityError &&
      err.kind === "invalidMasterKeyLength"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(err)}`);
  }
}

async function testMasterKeyRejects33Bytes(): Promise<TestCase> {
  const name = "GroupMasterKey rejects 33-byte raw value";
  try {
    new GroupMasterKey(new Uint8Array(33));
    return fail(name, "constructor did not throw");
  } catch (err) {
    if (
      err instanceof GroupIdentityError &&
      err.kind === "invalidMasterKeyLength"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(err)}`);
  }
}

async function testDerivePublicIdIsDeterministic(): Promise<TestCase> {
  const name =
    "derivePublicId is deterministic (same masterKey → same publicId)";
  const mk = await freshMasterKey();
  const id1 = await derivePublicId(mk);
  const id2 = await derivePublicId(mk);
  const ok = id1.equals(id2) && id1.raw.length === 16;
  return ok
    ? pass(name)
    : fail(name, `id1=${id1.asString()} id2=${id2.asString()}`);
}

async function testDerivePublicIdDiffersForDifferentKeys(): Promise<TestCase> {
  const name = "derivePublicId differs for different master keys";
  const a = await freshMasterKey();
  const b = await freshMasterKey();
  const idA = await derivePublicId(a);
  const idB = await derivePublicId(b);
  const ok = !idA.equals(idB);
  return ok
    ? pass(name)
    : fail(name, `collided on ${idA.asString()}`);
}

// ---------------------------------------------------------------------------
// Group B: group-key signing
// ---------------------------------------------------------------------------

async function testSignVerifyRoundtrip(): Promise<TestCase> {
  const name = "sign + verify roundtrip succeeds and returns a 32-byte MAC";
  const mk = await freshMasterKey();
  const payload = new TextEncoder().encode("hello group");
  const sig = await sign(mk, payload);
  const ok = sig.length === 32 && (await verify(mk, sig, payload));
  return ok ? pass(name) : fail(name, `sig.length=${sig.length}`);
}

async function testSignatureDiffersFromPayload(): Promise<TestCase> {
  const name = "signature is a 32-byte MAC, not the payload itself";
  const mk = await freshMasterKey();
  const payload = new TextEncoder().encode("hello group");
  const sig = await sign(mk, payload);
  const payloadBytes = new Uint8Array(payload.buffer);
  let same = sig.length === payloadBytes.length;
  if (same) {
    for (let i = 0; i < sig.length; i++) {
      if (sig[i] !== payloadBytes[i]) {
        same = false;
        break;
      }
    }
  }
  const ok = !same && sig.length === 32;
  return ok
    ? pass(name)
    : fail(name, `sig matches payload or wrong length (${sig.length})`);
}

async function testVerifyRejectsTamperedPayload(): Promise<TestCase> {
  const name = "verify rejects a tampered payload";
  const mk = await freshMasterKey();
  const payload = new TextEncoder().encode("hello group");
  const sig = await sign(mk, payload);
  const tampered = new TextEncoder().encode("hello group!");
  const ok = !(await verify(mk, sig, tampered));
  return ok ? pass(name) : fail(name, "tampered payload verified");
}

async function testVerifyRejectsWrongKey(): Promise<TestCase> {
  const name = "verify rejects a signature made with a different master key";
  const keyA = await freshMasterKey();
  const keyB = await freshMasterKey();
  const payload = new TextEncoder().encode("hello group");
  const sigFromA = await sign(keyA, payload);
  const ok = !(await verify(keyB, sigFromA, payload));
  return ok ? pass(name) : fail(name, "key-B accepted key-A's MAC");
}

async function testVerifyRejectsTruncatedSignature(): Promise<TestCase> {
  const name = "verify rejects a truncated 31-byte signature";
  const mk = await freshMasterKey();
  const payload = new TextEncoder().encode("hello group");
  const sig = await sign(mk, payload);
  const truncated = sig.slice(0, 31);
  const ok = !(await verify(mk, truncated, payload));
  return ok ? pass(name) : fail(name, "truncated sig accepted");
}

async function testVerifyRejectsOverlongSignature(): Promise<TestCase> {
  const name = "verify rejects an overlong 33-byte signature";
  const mk = await freshMasterKey();
  const payload = new TextEncoder().encode("hello group");
  const sig = await sign(mk, payload);
  const overlong = new Uint8Array(33);
  overlong.set(sig);
  overlong[32] = 0x00;
  const ok = !(await verify(mk, overlong, payload));
  return ok ? pass(name) : fail(name, "overlong sig accepted");
}

// ---------------------------------------------------------------------------
// Group C: invite link codec
// ---------------------------------------------------------------------------

async function testEncodeDecodeRoundtrip(): Promise<TestCase> {
  const name = "encode → decode roundtrip preserves both halves";
  const mk = await freshMasterKey();
  try {
    const encoded = await encodeGroupInviteLink(mk);
    const decoded = await decodeGroupInviteLink(encoded);
    const expectedPid = await derivePublicId(mk);
    const ok =
      decoded.masterKey.equals(mk) && decoded.publicId.equals(expectedPid);
    return ok
      ? pass(name)
      : fail(
          name,
          `mkMatch=${decoded.masterKey.equals(mk)} pidMatch=${decoded.publicId.equals(expectedPid)}`,
        );
  } catch (err) {
    return fail(name, String(err));
  }
}

async function testEncodedLinkHasV1Prefix(): Promise<TestCase> {
  const name = "encoded link starts with kaether:group:invite:v1: prefix";
  const mk = await freshMasterKey();
  const encoded = await encodeGroupInviteLink(mk);
  const ok = encoded.startsWith(GROUP_INVITE_PREFIX);
  return ok ? pass(name) : fail(name, `got ${encoded.slice(0, 40)}`);
}

async function testIsInviteLinkDistinguishes(): Promise<TestCase> {
  const name =
    "isGroupInviteLink distinguishes invite from other-codec strings";
  const mk = await freshMasterKey();
  const encoded = await encodeGroupInviteLink(mk);
  const positive = isGroupInviteLink(encoded);
  const negChat = isGroupInviteLink("just chat");
  const negEnvelope = isGroupInviteLink("kaether:e2ee:v1:abc");
  const negReceipt = isGroupInviteLink("kaether:receipt:v1:abc");
  const negTombstone = isGroupInviteLink("kaether:tombstone:v1:abc");
  const ok =
    positive && !negChat && !negEnvelope && !negReceipt && !negTombstone;
  return ok
    ? pass(name)
    : fail(
        name,
        `pos=${positive} chat=${negChat} env=${negEnvelope} rcpt=${negReceipt} tomb=${negTombstone}`,
      );
}

async function testTryDecodeReturnsNullForNonInvite(): Promise<TestCase> {
  const name = "tryDecodeGroupInviteLink returns null for non-invite strings";
  try {
    const r = await tryDecodeGroupInviteLink("hello world");
    const ok = r === null;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(r)}`);
  } catch (err) {
    return fail(name, `threw: ${String(err)}`);
  }
}

async function testTryDecodeReturnsLinkForValid(): Promise<TestCase> {
  const name =
    "tryDecodeGroupInviteLink returns the link for a valid invite";
  const mk = await freshMasterKey();
  const encoded = await encodeGroupInviteLink(mk);
  try {
    const r = await tryDecodeGroupInviteLink(encoded);
    const ok = r !== null && r.masterKey.equals(mk);
    return ok
      ? pass(name)
      : fail(name, `got ${JSON.stringify(r)}`);
  } catch (err) {
    return fail(name, `threw: ${String(err)}`);
  }
}

/**
 * CRITICAL rewrite-attack test: construct a hostile link by hand — keep the
 * original publicId bytes but XOR-flip a byte in the masterKey half.
 * derivePublicId(tamperedMk) will not match the embedded publicId, so decode
 * must throw publicIdDoesNotMatchMasterKey.
 */
async function testTamperedLinkRejected(): Promise<TestCase> {
  const name =
    "tampered invite link rejected (publicIdDoesNotMatchMasterKey)";
  const mk = await freshMasterKey();
  const publicId = await derivePublicId(mk);

  // Build a payload with a flipped master key but the original public id.
  const tamperedMkBytes = mk.raw.slice();
  tamperedMkBytes[0] ^= 0xff;
  const payload = new Uint8Array(48);
  payload.set(tamperedMkBytes, 0);
  payload.set(publicId.raw, 32);
  const tamperedLink = GROUP_INVITE_PREFIX + base64UrlEncode(payload);

  try {
    await decodeGroupInviteLink(tamperedLink);
    return fail(name, "decode did not throw");
  } catch (err) {
    if (
      err instanceof GroupIdentityError &&
      err.kind === "publicIdDoesNotMatchMasterKey"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error kind: ${String(err)}`);
  }
}

async function testGarbageBase64Rejected(): Promise<TestCase> {
  const name = "garbage base64url in invite link is rejected";
  const bad = GROUP_INVITE_PREFIX + "@@@not-base64@@@";
  try {
    await decodeGroupInviteLink(bad);
    return fail(name, "decode did not throw");
  } catch (err) {
    if (
      err instanceof GroupIdentityError &&
      err.kind === "invalidInviteLink"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error kind: ${String(err)}`);
  }
}

async function testWrongPrefixReturnsNull(): Promise<TestCase> {
  const name = "wrong prefix → null from tryDecodeGroupInviteLink";
  const r = await tryDecodeGroupInviteLink("kaether:call:link:v1:abc123");
  const ok = r === null;
  return ok ? pass(name) : fail(name, "expected null");
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Group identity (master key + invites)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = await Promise.all([
      // Group A
      testGenerateReturns32Bytes(),
      testTwoGeneratesDiffer(),
      testMasterKeyRejects31Bytes(),
      testMasterKeyRejects33Bytes(),
      testDerivePublicIdIsDeterministic(),
      testDerivePublicIdDiffersForDifferentKeys(),
      // Group B
      testSignVerifyRoundtrip(),
      testSignatureDiffersFromPayload(),
      testVerifyRejectsTamperedPayload(),
      testVerifyRejectsWrongKey(),
      testVerifyRejectsTruncatedSignature(),
      testVerifyRejectsOverlongSignature(),
      // Group C
      testEncodeDecodeRoundtrip(),
      testEncodedLinkHasV1Prefix(),
      testIsInviteLinkDistinguishes(),
      testTryDecodeReturnsNullForNonInvite(),
      testTryDecodeReturnsLinkForValid(),
      testTamperedLinkRejected(),
      testGarbageBase64Rejected(),
      testWrongPrefixReturnsNull(),
    ]);
    return buildSuiteResult(cases);
  },
});
